import { useState } from "react";
import { Alert, Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import { TELECOM_OPTIONS } from "./telecom";
import TermsConsent from "./TermsConsent";
import { IDENTITY_TERMS, allRequiredAgreed } from "./terms";

/**
 * 본인인증 입력 묶음. **로그인 화면과 회원가입 신청 화면이 같은 것을 쓴다.**
 *
 * 두 화면이 각자 그리면 한쪽만 고쳤을 때 조용히 갈라진다 — 통신사 목록을 한쪽에만
 * 추가하거나, 번호 정규화를 한쪽만 하는 식이다. 실제로 서버에서 그 일이 있었다
 * (`rqstCausCd` 를 한 곳에서만 채웠다).
 */
export type IdentityValues = {
  name: string;
  /** 주민등록번호 앞 6자리. */
  regFront: string;
  /**
   * 주민등록번호 **뒤 첫 한 글자**.
   *
   * 이 한 글자가 세기·성별·내외국인을 동시에 정한다. 생년월일 8자리만 받으면
   * **성별을 알 수 없어서** 서버가 `x` 를 보내게 되고, KCB 가 그것을 거절한다.
   * 실제로 운영에서 막혔다.
   *
   * 나머지 여섯 자리는 **받지 않는다.** 쓸 데가 없으면서 유출되면 되돌릴 수 없다.
   */
  regRear: string;
  telComCd: string;
  telNo: string;
  otpNo: string;
};

export const EMPTY_IDENTITY: IdentityValues = {
  name: "",
  regFront: "",
  regRear: "",
  telComCd: "",
  telNo: "",
  otpNo: "",
};

/** 인증번호를 요청할 수 있는 상태인가. 서버가 다시 보지만 여기서 먼저 걸러 준다. */
export const canRequestOtp = (values: IdentityValues): boolean =>
  values.name.trim().length > 0 &&
  values.regFront.length === 6 &&
  values.regRear.length === 1 &&
  values.telComCd !== "" &&
  values.telNo.replace(/\D/g, "").length >= 10;

type Props = {
  values: IdentityValues;
  onChange: (next: IdentityValues) => void;
  /** 인증번호를 이미 받았는가. 받은 뒤에만 입력칸을 띄운다. */
  sent: boolean;
  sending: boolean;
  /**
   * 인증번호 발송. **동의한 항목을 같이 넘긴다.**
   *
   * 서버가 이 목록을 다시 본다 — 화면에서만 막으면 API 를 직접 부르는 순간
   * 통과하는데, 서버는 KCB 에 `agree1~4` 를 전부 `Y` 로 보낸다. 받지 않은 동의를
   * 받았다고 선언하게 된다.
   *
   * 인수로 넘기는 이유는 **호출부가 잊을 수 없게** 하기 위해서다. 동의 상태를
   * 밖으로 빼면 세 화면이 각자 들고 각자 빠뜨린다.
   */
  onRequestOtp: (agreedTerms: string[]) => void;
  error?: string;
  disabled?: boolean;
  /**
   * 이름 칸을 숨긴다. 회원가입 신청 폼은 **위에서 이미 이름을 받으므로** 여기서
   * 또 받으면 두 값이 갈라진다 — 인증 이름과 신청 이름이 다르면 승인하는 사람이
   * «누구지» 를 겪는다.
   */
  hideName?: boolean;
};

export default function IdentityFields({
  values, onChange, sent, sending, onRequestOtp, error, disabled, hideName,
}: Props) {
  /**
   * 동의 상태를 **안에서** 들고 있다.
   *
   * 서버는 `agree1~4` 를 항상 `Y` 로 보낸다 — 바깥이 이 값을 알아야 할 일이 없다.
   * 밖으로 빼면 세 화면(로그인·가입신청·이메일찾기)이 각자 들고 각자 잊는다.
   */
  const [agreed, setAgreed] = useState<Record<string, boolean>>({});
  const consented = allRequiredAgreed(agreed);

  const set = (patch: Partial<IdentityValues>) => onChange({ ...values, ...patch });
  const digits = (text: string, max: number) => text.replace(/\D/g, "").slice(0, max);

  return (
    <Stack spacing={2}>
      {!hideName && (
        <TextField
          fullWidth
          label="이름"
          autoComplete="name"
          // 서버는 36자에서 422 를 준다. 화면이 먼저 막는다(f1-4).
          inputProps={{ maxLength: 36 }}
          value={values.name}
          onChange={(event) => set({ name: event.target.value })}
          disabled={disabled}
        />
      )}
      {/* 주민등록번호. **앞 6자리와 뒤 첫 한 글자만** 받는다.
          나머지 여섯 자리는 인증에 쓰이지 않으므로 받지 않는다. */}
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          fullWidth
          label="주민등록번호 앞 6자리"
          placeholder="생년월일 6자리"
          inputProps={{ inputMode: "numeric" }}
          value={values.regFront}
          onChange={(event) => set({ regFront: digits(event.target.value, 6) })}
          disabled={disabled}
        />
        <Typography sx={{ color: "text.secondary" }}>—</Typography>
        <TextField
          label="뒤 1자리"
          placeholder="1"
          inputProps={{ inputMode: "numeric", style: { textAlign: "center" } }}
          value={values.regRear}
          onChange={(event) => set({ regRear: digits(event.target.value, 1) })}
          disabled={disabled}
          sx={{ width: 110, flexShrink: 0 }}
          helperText="●●●●●●"
          FormHelperTextProps={{ sx: { textAlign: "center", letterSpacing: 2, mx: 0 } }}
        />
      </Stack>
      <TextField
        select
        fullWidth
        label="통신사"
        value={values.telComCd}
        onChange={(event) => set({ telComCd: event.target.value })}
        disabled={disabled}
      >
        {TELECOM_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        fullWidth
        label="휴대폰번호"
        placeholder="01012345678"
        inputProps={{ inputMode: "tel" }}
        autoComplete="tel"
        value={values.telNo}
        onChange={(event) => set({ telNo: digits(event.target.value, 11) })}
        disabled={disabled}
      />
      {/* 동의는 **발송 버튼 위**에 둔다. 발송은 문자가 나가고 과금되는 되돌릴 수
          없는 행위라, 누른 뒤에 «동의하세요» 를 만나면 이미 늦다. */}
      <TermsConsent agreed={agreed} onChange={setAgreed} disabled={disabled} />
      {/* 누를 수 없는 이유를 **막는 것으로** 알린다. 눌렀다가 «먼저 입력하세요»
          를 보는 것보다 낫다 — 부르는 것 자체가 문자 발송이고 과금이다. */}
      <LoadingButton
        fullWidth
        size="large"
        variant="outlined"
        loading={sending}
        disabled={disabled || !consented || !canRequestOtp(values)}
        onClick={() => onRequestOtp(IDENTITY_TERMS.filter((item) => agreed[item.key]).map((item) => item.key))}
      >
        {sent ? "인증번호 다시 받기" : "인증번호 받기"}
      </LoadingButton>
      {/* 버튼이 왜 안 눌리는지 **바로 아래에서** 말한다. 이유가 둘(빈 칸·동의)이라
          «모든 칸을 채우면» 만 띄우면 다 채운 사람이 이유를 못 찾는다. */}
      {!sent && !(consented && canRequestOtp(values)) && (
        <Typography variant="caption" sx={{ color: "text.secondary", mt: -1 }}>
          {canRequestOtp(values)
            ? "필수 약관에 동의하시면 인증번호를 받을 수 있습니다."
            : "모든 칸을 채우고 약관에 동의하시면 인증번호를 받을 수 있습니다."}
        </Typography>
      )}
      {/* 인증번호 칸은 **보낸 뒤에만** 뜬다. 처음부터 보이면 «어디서 받지» 를 먼저
          묻게 된다. */}
      {sent && (
        <TextField
          fullWidth
          label="인증번호"
          inputProps={{ inputMode: "numeric" }}
          autoComplete="one-time-code"
          value={values.otpNo}
          onChange={(event) => set({ otpNo: digits(event.target.value, 6) })}
          disabled={disabled}
        />
      )}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}

/** 폼이 비어 있을 때 되돌리는 버튼. 잘못 친 값을 지우는 가장 빠른 길이다. */
export function ResetIdentityButton({ onReset }: { onReset: () => void }) {
  return (
    <Button size="small" onClick={onReset}>
      정보를 다시 입력하기
    </Button>
  );
}
